<template>
    <div v-if="currentView === 'outgoing_approval'">
        <div class="page-header">
            <div>
                <div class="page-title">寄件前審核</div>
                <div class="page-subtitle">管理需要主管核准才能送出的寄件請求</div>
            </div>
        </div>

        <!-- Tabs -->
        <div class="card p-4 mb-6">
            <div class="flex flex-wrap gap-2 items-center">
                <button @click="approvalTab = 'pending'" :class="approvalTab === 'pending' ? 'active' : ''" class="tab-item">
                    待審核
                    <span v-if="outgoingApprovalPendingCount > 0" class="badge badge-warning" style="margin-left:0.375rem;">{{ outgoingApprovalPendingCount }}</span>
                </button>
                <button @click="approvalTab = 'all'; fetchOutgoingApprovals()" :class="approvalTab === 'all' ? 'active' : ''" class="tab-item">全部紀錄</button>
                <button v-if="isSuperAdmin || isDomainAdmin" @click="approvalTab = 'settings'" :class="approvalTab === 'settings' ? 'active' : ''" class="tab-item">⚙ 審核設定</button>
                <div class="ml-auto flex gap-2">
                    <button @click="fetchOutgoingApprovals()" :disabled="approvalLoading" class="btn btn-outline btn-sm">
                        {{ approvalLoading ? '載入中...' : '重新整理' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Pending / All list -->
        <div v-if="approvalTab !== 'settings'" class="card p-6">
            <!-- Filters -->
            <div class="flex flex-wrap gap-3 items-end mb-4">
                <div>
                    <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">狀態</label>
                    <select v-model="approvalFilter.status" class="input w-40" @change="fetchOutgoingApprovals()">
                        <option value="">全部</option>
                        <option value="pending_approval">待審核</option>
                        <option value="approved">已核准</option>
                        <option value="rejected">已退回</option>
                        <option value="sent">已送出</option>
                        <option value="send_failed">送出失敗</option>
                    </select>
                </div>
                <div v-if="isSuperAdmin || isDomainAdmin">
                    <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">寄件者</label>
                    <input v-model="approvalFilter.sender" class="input w-52" placeholder="email" @keyup.enter="fetchOutgoingApprovals()">
                </div>
                <button @click="fetchOutgoingApprovals()" class="btn btn-primary btn-sm">查詢</button>
            </div>

            <div v-if="approvalLoading" class="empty-state py-12"><span class="spinner"></span></div>
            <div v-else-if="approvalList.length === 0" class="empty-state py-12">
                <p>目前沒有{{ approvalFilter.status === 'pending_approval' ? '待審核' : '' }}記錄</p>
            </div>
            <div v-else class="overflow-x-auto">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>寄件者</th>
                            <th>收件者</th>
                            <th>主旨</th>
                            <th>狀態</th>
                            <th>建立時間</th>
                            <th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="r in approvalList" :key="r.id">
                            <td style="font-size:0.8125rem;">{{ r.sender_email }}</td>
                            <td style="font-size:0.8125rem;max-width:12rem;" class="truncate">{{ r.to }}</td>
                            <td style="font-size:0.8125rem;max-width:16rem;" class="truncate">{{ r.subject || '(無主旨)' }}</td>
                            <td>
                                <span class="badge"
                                    :class="{
                                        'badge-warning':  r.status === 'pending_approval',
                                        'badge-success':  r.status === 'approved' || r.status === 'sent',
                                        'badge-danger':   r.status === 'rejected' || r.status === 'send_failed',
                                        'badge-default':  !['pending_approval','approved','sent','rejected','send_failed'].includes(r.status),
                                    }">
                                    {{ { pending_approval:'待審核', approved:'已核准', rejected:'已退回', sent:'已送出', send_failed:'送出失敗' }[r.status] || r.status }}
                                </span>
                            </td>
                            <td style="font-size:0.8125rem;white-space:nowrap;color:var(--gray-500);">
                                {{ r.created_at ? new Date(r.created_at).toLocaleString() : '—' }}
                            </td>
                            <td class="whitespace-nowrap">
                                <button @click="openApprovalDetail(r)" class="btn btn-xs btn-outline mr-1">查看</button>
                                <button v-if="r.status === 'pending_approval' && (isSuperAdmin || isDomainAdmin)"
                                    @click="approveRequest(r.id)" class="btn btn-xs btn-success mr-1">核准</button>
                                <button v-if="r.status === 'pending_approval' && (isSuperAdmin || isDomainAdmin)"
                                    @click="openRejectModal(r)" class="btn btn-xs btn-outline" style="color:var(--color-danger);border-color:#fca5a5;">退回</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Settings Tab -->
        <div v-if="approvalTab === 'settings'" class="card p-6">
            <div class="card-title mb-4">寄件審核帳號設定</div>
            <div style="font-size:0.875rem;color:var(--gray-500);margin-bottom:1rem;">
                設定哪些使用者的寄件需要主管核准，以及對應的核准主管。
            </div>
            <div v-if="approvalSettingsLoading" class="empty-state py-8"><span class="spinner"></span></div>
            <div v-else class="overflow-x-auto">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>使用者</th>
                            <th>啟用寄件審核</th>
                            <th>審核範圍</th>
                            <th>核准主管</th>
                            <th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="u in approvalSettingsUsers" :key="u.id">
                            <td>{{ u.email }}</td>
                            <td>
                                <input type="checkbox" :checked="!!u.requires_outgoing_approval"
                                    @change="toggleUserApproval(u, $event.target.checked)">
                            </td>
                            <td>
                                <select v-if="u.requires_outgoing_approval"
                                    :value="u.outgoing_approval_scope || 'external_only'"
                                    @change="updateUserApprovalScope(u, $event.target.value)"
                                    class="input" style="width:auto;">
                                    <option value="external_only">僅外部收件人</option>
                                    <option value="all">全部寄件</option>
                                </select>
                                <span v-else style="color:var(--gray-400);">—</span>
                            </td>
                            <td>
                                <select v-if="u.requires_outgoing_approval"
                                    :value="u.approver_user_id || ''"
                                    @change="updateUserApprover(u, $event.target.value)"
                                    class="input" style="width:auto;">
                                    <option value="">（未設定）</option>
                                    <option v-for="admin in domainAdmins" :key="admin.id" :value="admin.id">{{ admin.email }}</option>
                                </select>
                                <span v-else style="color:var(--gray-400);">—</span>
                            </td>
                            <td>
                                <span v-if="approvalSettingsSaving === u.id" class="spinner" style="width:14px;height:14px;border-width:2px;"></span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Detail Modal -->
        <div v-if="approvalDetailModal.open" class="modal-overlay" style="z-index:9999;">
            <div class="modal-box w-full max-w-2xl" style="max-height:90vh;display:flex;flex-direction:column;">
                <div class="modal-header">
                    <h3 class="modal-title">寄件審核詳情 #{{ approvalDetailModal.data?.id }}</h3>
                    <button @click="approvalDetailModal.open = false" class="btn btn-ghost btn-sm">×</button>
                </div>
                <div class="modal-body flex-1 overflow-y-auto space-y-3" v-if="approvalDetailModal.data">
                    <div class="grid grid-cols-2 gap-3" style="font-size:0.875rem;">
                        <div><span style="color:var(--gray-500);">寄件者：</span><strong>{{ approvalDetailModal.data.sender_email }}</strong></div>
                        <div><span style="color:var(--gray-500);">狀態：</span>
                            <span class="badge" :class="{'badge-warning': approvalDetailModal.data.status==='pending_approval','badge-success':approvalDetailModal.data.status==='sent','badge-danger':approvalDetailModal.data.status==='rejected'}">
                                {{ approvalDetailModal.data.status }}
                            </span>
                        </div>
                        <div class="col-span-2"><span style="color:var(--gray-500);">收件者：</span>{{ approvalDetailModal.data.to }}</div>
                        <div v-if="approvalDetailModal.data.cc" class="col-span-2"><span style="color:var(--gray-500);">副本：</span>{{ approvalDetailModal.data.cc }}</div>
                        <div class="col-span-2"><span style="color:var(--gray-500);">主旨：</span><strong>{{ approvalDetailModal.data.subject }}</strong></div>
                    </div>
                    <div>
                        <div style="font-size:0.75rem;font-weight:700;color:var(--gray-600);margin-bottom:0.375rem;">郵件內容</div>
                        <div class="input" style="min-height:8rem;white-space:pre-wrap;font-size:0.8125rem;overflow-y:auto;max-height:20rem;">
                            {{ approvalDetailModal.data.body || '（無純文字內容）' }}
                        </div>
                    </div>
                    <div v-if="approvalDetailModal.data.decision_note">
                        <div style="font-size:0.75rem;font-weight:700;color:var(--gray-600);margin-bottom:0.375rem;">審核意見</div>
                        <div class="input" style="font-size:0.8125rem;">{{ approvalDetailModal.data.decision_note }}</div>
                    </div>
                </div>
                <div class="modal-footer" v-if="approvalDetailModal.data">
                    <button @click="approvalDetailModal.open = false" class="btn btn-ghost btn-sm">關閉</button>
                    <template v-if="approvalDetailModal.data.status === 'pending_approval' && (isSuperAdmin || isDomainAdmin)">
                        <button @click="approveRequest(approvalDetailModal.data.id); approvalDetailModal.open = false" class="btn btn-success">核准送出</button>
                        <button @click="openRejectModal(approvalDetailModal.data); approvalDetailModal.open = false" class="btn btn-outline" style="color:var(--color-danger);border-color:#fca5a5;">退回</button>
                    </template>
                </div>
            </div>
        </div>

        <!-- Reject Modal -->
        <div v-if="rejectModal.open" class="modal-overlay" style="z-index:10000;">
            <div class="modal-box w-full max-w-md">
                <div class="modal-header">
                    <h3 class="modal-title">退回寄件請求</h3>
                    <button @click="rejectModal.open = false" class="btn btn-ghost btn-sm">×</button>
                </div>
                <div class="modal-body space-y-3">
                    <div>
                        <label class="block text-xs font-medium mb-1" style="color:var(--gray-600)">退回原因（將通知寄件者）</label>
                        <textarea v-model="rejectModal.note" class="input" style="height:6rem;resize:vertical;" placeholder="請說明退回原因..."></textarea>
                    </div>
                </div>
                <div class="modal-footer">
                    <button @click="rejectModal.open = false" class="btn btn-ghost btn-sm">取消</button>
                    <button @click="rejectRequest(rejectModal.requestId, rejectModal.note)" :disabled="approvalActionLoading" class="btn btn-danger">
                        <span v-if="approvalActionLoading" class="spinner spinner-sm"></span>
                        確認退回
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '../store/app.js'

const store = useAppStore()
const { currentView, isSuperAdmin, isDomainAdmin, outgoingApprovalPendingCount, users } = storeToRefs(store)
const { canAccess, fetchUsers } = store

const approvalTab = ref('pending')
const approvalLoading = ref(false)
const approvalList = ref([])
const approvalFilter = ref({ status: 'pending_approval', sender: '' })
const approvalDetailModal = ref({ open: false, data: null })
const rejectModal = ref({ open: false, requestId: null, note: '' })
const approvalActionLoading = ref(false)
const approvalSettingsLoading = ref(false)
const approvalSettingsUsers = ref([])
const approvalSettingsSaving = ref(null)

import axios from 'axios'
const ADMIN_API = store.ADMIN_API || (window.__ADMIN_API__ || '/api')

const domainAdmins = ref([])

async function fetchOutgoingApprovals() {
    approvalLoading.value = true
    try {
        const params = {}
        if (approvalFilter.value.status) params.status = approvalFilter.value.status
        if (approvalFilter.value.sender) params.sender_email = approvalFilter.value.sender
        const res = await axios.get(`${ADMIN_API}/outgoing-approvals/pending`, { params })
        approvalList.value = res.data || []
        store.outgoingApprovalPendingCount = approvalList.value.filter(r => r.status === 'pending_approval').length
    } catch (e) {
        window.$toast?.error('載入失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        approvalLoading.value = false
    }
}

async function openApprovalDetail(r) {
    try {
        const res = await axios.get(`${ADMIN_API}/outgoing-approvals/${r.id}`)
        approvalDetailModal.value = { open: true, data: res.data }
    } catch (e) {
        window.$toast?.error('載入詳情失敗')
    }
}

async function approveRequest(id) {
    approvalActionLoading.value = true
    try {
        await axios.post(`${ADMIN_API}/outgoing-approvals/${id}/approve`, {})
        window.$toast?.success('已核准，郵件將送出')
        await fetchOutgoingApprovals()
    } catch (e) {
        window.$toast?.error('核准失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        approvalActionLoading.value = false
    }
}

function openRejectModal(r) {
    rejectModal.value = { open: true, requestId: r.id, note: '' }
}

async function rejectRequest(id, note) {
    approvalActionLoading.value = true
    try {
        await axios.post(`${ADMIN_API}/outgoing-approvals/${id}/reject`, { note: note || '' })
        window.$toast?.success('已退回')
        rejectModal.value.open = false
        await fetchOutgoingApprovals()
    } catch (e) {
        window.$toast?.error('退回失敗: ' + (e.response?.data?.detail || e.message))
    } finally {
        approvalActionLoading.value = false
    }
}

async function loadApprovalSettings() {
    approvalSettingsLoading.value = true
    try {
        if (!users.value || users.value.length === 0) await fetchUsers()
        approvalSettingsUsers.value = (users.value || []).filter(u => u.role !== 'super_admin')
        domainAdmins.value = (users.value || []).filter(u => u.role === 'domain_admin' || u.role === 'super_admin')
    } finally {
        approvalSettingsLoading.value = false
    }
}

async function toggleUserApproval(u, enabled) {
    approvalSettingsSaving.value = u.id
    try {
        await axios.put(`${ADMIN_API}/users/${u.id}`, { requires_outgoing_approval: enabled })
        u.requires_outgoing_approval = enabled
        window.$toast?.success('設定已儲存')
    } catch (e) {
        window.$toast?.error('儲存失敗')
    } finally {
        approvalSettingsSaving.value = null
    }
}

async function updateUserApprovalScope(u, scope) {
    approvalSettingsSaving.value = u.id
    try {
        await axios.put(`${ADMIN_API}/users/${u.id}`, { outgoing_approval_scope: scope })
        u.outgoing_approval_scope = scope
    } catch (e) {
        window.$toast?.error('儲存失敗')
    } finally {
        approvalSettingsSaving.value = null
    }
}

async function updateUserApprover(u, approverId) {
    approvalSettingsSaving.value = u.id
    try {
        await axios.put(`${ADMIN_API}/users/${u.id}`, { approver_user_id: approverId || null })
        u.approver_user_id = approverId || null
    } catch (e) {
        window.$toast?.error('儲存失敗')
    } finally {
        approvalSettingsSaving.value = null
    }
}

// watch tab change
import { watch } from 'vue'
watch(approvalTab, (tab) => {
    if (tab === 'settings') loadApprovalSettings()
    else fetchOutgoingApprovals()
})

onMounted(() => fetchOutgoingApprovals())
</script>
